import { Body1, tokens } from '@fluentui/react-components';
import { IChatMessage } from '../../libs/models/ChatMessage';

/*
 * Function to check if date is today.
 */
export function isToday(date: Date) {
    return date.toDateString() === new Date().toDateString();
}

export function isInLastWeek(date: Date) {
    const now = new Date();
    const sevenDaysAgo = new Date(now.setDate(now.getDate() - 7));
    return date >= sevenDaysAgo;
}

export function isInLastMonth(date: Date) {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));
    return date >= thirtyDaysAgo;
}

/*
 * Function to render the date and/or time of a message.
 */
export function timestampToDateString(timestamp: number, alwaysShowTime = false) {
    const date = new Date(timestamp);
    const dateString = date.toLocaleDateString([], {
        month: 'numeric',
        day: 'numeric',
    });
    const timeString = date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
    });

    return date.toDateString() !== new Date().toDateString()
        ? alwaysShowTime
            ? dateString + ' ' + timeString // if the date is not today and we are always showing the time, show the date and time
            : dateString // if the date is not today and we are not always showing the time, only show the date
        : timeString; // if the date is today, only show the time
}

/*
 * Function to create a command link
 */
export function createCommandLink(command: string) {
    const escapedCommand = encodeURIComponent(command);
    const createCommandLink = `<span style="text-decoration: underline; cursor: pointer" data-command="${escapedCommand}" onclick="(function(){ let chatInput = document.getElementById('chat-input'); chatInput.value = decodeURIComponent('${escapedCommand}'); chatInput.focus(); return false; })();return false;">${command}</span>`;
    return createCommandLink;
}

/*
 * Function to format chat text content to remove any html tags from it.
 */
export function formatChatTextContent(messageContent: string) {
    const contentAsString = messageContent
        .trim()
        .replace(/^sk:\/\/.*$/gm, (match: string) => createCommandLink(match))
        .replace(/^!sk:.*$/gm, (match: string) => createCommandLink(match));
    return contentAsString;
}

/*
 * Formats text containing `\n` or `\r` into paragraphs.
 */
export function formatParagraphTextContent(messageContent = '') {
    messageContent = messageContent.replaceAll('\r\n', '\n\r');

    return (
        <Body1>
            {messageContent.split('\n').map((paragraph, idx) => (
                <p
                    key={`paragraph-${idx}`}
                    style={
                        paragraph.includes('\r')
                            ? {
                                  display: 'flex',
                                  marginLeft: tokens.spacingHorizontalL,
                              }
                            : {
                                  overflowWrap: 'anywhere',
                              }
                    }
                >
                    {paragraph}
                </p>
            ))}
        </Body1>
    );
}

/*
 * Function to replace citation links with indices matching the citation list.
 */
export function replaceCitationLinksWithIndices(formattedMessageContent: string, message: IChatMessage) {
    const citations = message.citations;
    if (citations) {
        citations.forEach((citation, index) => {
            const citationLink = citation.link;
            formattedMessageContent = formattedMessageContent.replaceAll(citationLink, (index + 1).toString());
        });
    }

    return formattedMessageContent;
}

/**
 * Gets message of error
 */
export function getErrorDetails(error: unknown) {
    return error instanceof Error ? error.message : String(error);
}

/**
 * Inserts text at the cursor position in a string
 *
 * @param {string} originalText - The original text
 * @param {string} insertionText - The text to insert
 * @param {number} cursorPosition - The position of the cursor in the original text
 * @param {boolean} replaceLastChar - Whether to replace the last character before the cursor (useful for trigger characters)
 * @returns {object} Object containing the new text and the new cursor position
 */
export const insertTextAtCursor = (
  originalText = '',
  insertionText = '',
  cursorPosition = 0,
  replaceLastChar = false
) => {
  // Ensure we're working with strings
  const text = String(originalText);
  const insertion = String(insertionText);

  // Get text before and after cursor
  const textBeforeCursor = text.substring(0, cursorPosition);
  const textAfterCursor = text.substring(cursorPosition);

  // If we need to replace the last character (e.g., the trigger character)
  const newTextBeforeCursor = replaceLastChar && textBeforeCursor.length > 0
    ? textBeforeCursor.slice(0, -1)
    : textBeforeCursor;

  // Create the new text
  const newText = newTextBeforeCursor + insertion + textAfterCursor;

  // Calculate the new cursor position (after the inserted text)
  const newCursorPosition = newTextBeforeCursor.length + insertion.length;

  return {
    text: newText,
    cursorPosition: newCursorPosition
  };
};

/**
 * Gets the display coordinates for a text position in a textarea
 *
 * @param {HTMLTextAreaElement} textAreaElement - The textarea element
 * @param {number} cursorPosition - The position in the text
 * @returns {object} The x and y coordinates for the position
 */
interface TextCoordinates {
    x: number;
    y: number;
}
export const getTextPositionCoordinates = (
    textAreaElement: HTMLTextAreaElement | null,
    cursorPosition: number,
): TextCoordinates => {
    if (!textAreaElement) {
        return { x: 0, y: 0 };
    }

    // Create a temporary mirror element to calculate position
    const mirror = document.createElement('div');

    // Copy styles from textarea to ensure accurate measurements
    const style = window.getComputedStyle(textAreaElement);

    // Apply relevant styles to the mirror
    const stylesToCopy = [
        'boxSizing',
        'width',
        'height',
        'padding',
        'border',
        'fontFamily',
        'fontSize',
        'fontWeight',
        'lineHeight',
        'letterSpacing',
        'textIndent',
        'whiteSpace',
        'wordWrap',
        'overflowWrap',
    ] as const;

    stylesToCopy.forEach((styleName) => {
        const value = style.getPropertyValue(styleName);
        mirror.style.setProperty(styleName, value);
    });

    // Set additional styles for accurate measurement
    mirror.style.position = 'absolute';
    mirror.style.top = '0';
    mirror.style.left = '0';
    mirror.style.visibility = 'hidden';
    mirror.style.overflow = 'auto';

    // Get text before cursor and create a span at the cursor position
    const textBeforeCursor = textAreaElement.value.substring(0, cursorPosition);
    mirror.textContent = textBeforeCursor;
    const span = document.createElement('span');
    span.textContent = '.'; // Add a character to measure position
    mirror.appendChild(span);

    // Add to document, measure, then remove
    document.body.appendChild(mirror);
    const coordinates = {
        x: span.offsetLeft,
        y: span.offsetTop,
    };
    document.body.removeChild(mirror);

    // Adjust for textarea's position
    const textAreaRect = textAreaElement.getBoundingClientRect();
    const scrollLeft = textAreaElement.scrollLeft;
    const scrollTop = textAreaElement.scrollTop;

    return {
        x: textAreaRect.left + coordinates.x - scrollLeft,
        y: textAreaRect.top + coordinates.y - scrollTop + span.offsetHeight,
    };
};

/**
 * Advanced truncate text function that handles HTML and special characters
 *
 * @param text - The input text to truncate
 * @param maxLength - Maximum length of the output text including ellipsis
 * @param options - Additional options for truncation
 * @returns Truncated text with ellipsis if needed
 */
interface TruncateOptions {
    preserveWords?: boolean;
    ellipsis?: string;
    stripHtml?: boolean;
}

export const truncateTextAdvanced = (text: string, maxLength = 50, options: TruncateOptions = {}): string => {
    const { preserveWords = true, ellipsis = '...', stripHtml = true } = options;

    let processedText = text;
    if (stripHtml) {
        processedText = text.replace(/<[^>]*>/g, '');
    }

    if (processedText.length <= maxLength) {
        return processedText;
    }

    const ellipsisLength = ellipsis.length;
    let truncated = processedText.slice(0, maxLength - ellipsisLength);

    if (preserveWords) {
        const lastSpace = truncated.lastIndexOf(' ');
        if (lastSpace !== -1) {
            truncated = truncated.slice(0, lastSpace);
        }
    }

    return truncated + ellipsis;
};

